import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu as MenuIcon, X as XIcon, ChevronDown, Rss, Newspaper, Calendar, ArrowRight, Search, BookOpen, Instagram, Youtube, Linkedin, Home, MapPin, Mail, Phone, Bell, FileText, PlayCircle, Building2, GraduationCap, Globe, Globe2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { trackNavigation, trackExternalClick, trackButtonClick } from '../../utils/analytics';
import DuyurularData from '../../data/Duyurular.json';
import './Navbar.css';

// navLinks fonksiyonu - dil desteği ile
const getNavLinks = (t, currentLang) => {
    const prefix = currentLang === 'en' ? '/en' : '';

    return [
        { title: t('nav.home'), href: prefix + "/" },
        { title: t('nav.institutional'), sublinks: [
                { title: t('nav.history'), href: prefix + "/kurumsal/tarihce" },
                { title: t('nav.missionVision'), href: prefix + "/kurumsal/misyon-vizyon" },
                { title: t('nav.fedek'), href: prefix + "/kurumsal/fedek" },
                { title: t('nav.strategicPlan'), href: prefix + "/kurumsal/stratejik-plan" },
                { title: t('nav.formerDeans'), href: prefix + "/kurumsal/eski-dekanlar" },
        ]},
        { title: t('nav.management'), sublinks: [
                { title: t('nav.dean'), href: prefix + "/yonetim/dekan" },
                { title: t('nav.viceDeans'), href: prefix + "/yonetim/dekan-yardimcilari" },
                { title: t('nav.facultySecretary'), href: prefix + "/yonetim/fakulte-sekreteri" },
                { title: t('nav.facultyBoard'), href: prefix + "/yonetim/fakulte-kurulu" },
                { title: t('nav.managementBoard'), href: prefix + "/yonetim/yonetim-kurulu" },
                { title: t('nav.unitActivities'), href: prefix + "/birim-faaliyetleri" },
        ]},
        { title: t('nav.boardsCommissions'), sublinks: [
                { title: t('nav.qualityStrategy'), href: prefix + "/kurul-komisyonlar/kalite-strateji" },
                { title: t('nav.academicQuality'), href: prefix + "/kurul-komisyonlar/akademik-birim-kalite" },
                { title: t('nav.scholarship'), href: prefix + "/kurul-komisyonlar/burs-komisyonu" },
                { title: t('nav.promotionCeremony'), href: prefix + "/kurul-komisyonlar/tanitim-toren" },
                { title: t('nav.publication'), href: prefix + "/kurul-komisyonlar/yayin" },
                { title: t('nav.nonFormalLearning'), href: prefix + "/kurul-komisyonlar/program-disi-ogrenme" },
                { title: t('nav.advisoryBoard'), href: prefix + "/kurul-komisyonlar/birim-danisma-kurulu" },
        ]},
        { title: t('nav.facultyDecisions'), sublinks: [
                { title: t('nav.facultyBoardDecisions'), href: prefix + "/kararlar/fakulte-kurulu" },
                { title: t('nav.managementBoardDecisions'), href: prefix + "/kararlar/fakulte-yonetim" },
        ]},
        { title: t('nav.facultyPersonnel'), sublinks: [
                { title: t('nav.faculty'), href: prefix + "/fakulte-personeli/ogretim-uyeleri" },
                { title: t('nav.administrativeStaff'), href: prefix + "/fakulte-personeli/idari-personel" },
                { title: t('nav.retiredFaculty'), href: prefix + "/fakulte-personeli/emekli-ogretim-elemanlari" },
        ]},
        { title: t('nav.research'), sublinks: [
            { title: t('nav.labs'), href: prefix + "/arastirma/laboratuvarlar" },
            { title: t('nav.books'), href: prefix + "/arastirma/kitaplar-editorlukler" },
            { title: t('nav.journals'), href: prefix + "/arastirma/dergiler" },
            { title: t('nav.museums'), href: prefix + "/arastirma/muzeler" },
            { title: t('nav.ongoingProjects'), href: prefix + "/arastirma/devam-eden-projelerimiz" },
        ]},
        { title: t('nav.student'), sublinks: [
            { title: t('nav.studentGuide'), href: "https://fs.hacettepe.edu.tr/fenyeni/dosyalar/ogrencikilavuzu/ogrencikilavuzu.pdf", external: true },
            { title: t('nav.usefulDocuments'), href: prefix + "/belgeler/ogrenci" },
            { title: t('nav.internship'), href: prefix + "/staj" },
            { title: t('nav.studentClubs'), href: prefix + "/ogrenci-topluluklari" },
            { title: t('nav.erasmus'), href: prefix + "/programlar/erasmus" },
            { title: t('nav.internationalStudents'), href: "https://oidb.hacettepe.edu.tr/", external: true },
        ]},

        { title: t('nav.departments'), sublinks: [
            { title: t('nav.actuarialScience'), href: "https://aktuerya.hacettepe.edu.tr", external: true },
            { title: t('nav.biology'), href: "https://biology.hacettepe.edu.tr", external: true },
            { title: t('nav.chemistry'), href: "https://chem.hacettepe.edu.tr", external: true },
            { title: t('nav.mathematics'), href: "https://mat.hacettepe.edu.tr", external: true },
            { title: t('nav.statistics'), href: "https://stat.hacettepe.edu.tr", external: true },
        ]},
        { title: t('nav.programs'), sublinks: [
            { title: t('nav.undergraduate'), href: prefix + "/programlar/lisans" },
            { title: t('nav.masters'), href: prefix + "/programlar/yuksek-lisans" },
            { title: t('nav.doctorate'), href: prefix + "/programlar/doktora" },
            { title: t('nav.minor'), href: prefix + "/programlar/yandal" },
            { title: t('nav.doubleMajor'), href: prefix + "/programlar/cift-anadal" },
            { title: t('nav.erasmus'), href: prefix + "/programlar/erasmus" },
        ]},
        { title: t('nav.documents'), sublinks: [
                { title: t('nav.lawsRegulations'), href: prefix + "/belgeler/kanun-yonetmelikler" },
                { title: t('nav.academicAdministrative'), href: prefix + "/belgeler/akademik-idari-personel" },
                { title: t('nav.student'), href: prefix + "/belgeler/ogrenci" },
        ]},
        { title: t('nav.accessibleLife'), href: prefix + "/engelsiz-yasam" },
        { title: t('nav.socialResponsibility'), href: prefix + "/sosyal-sorumluluk-projeleri" },
        { title: t('nav.gallery'), sublinks: [
            { title: t('nav.photoGallery'), href: prefix + "/albums" },
            { title: t('nav.videoGallery'), href: prefix + "/videogaleri" },
        ]},
        { title: t('nav.contact'), href: prefix + "/iletisim" },
    ];
};

// Bölüm tanıtımları verisi
const getDepartmentPromotions = (t) => [
    { title: t('navbar.facultyPromotion'), href: "https://www.youtube.com/watch?v=f7tTbfwei9Q", icon: "Youtube" },
    { title: t('navbar.biologyPromotion'), href: "https://www.youtube.com/watch?v=6OqEm9H9knk", icon: "Youtube" },
    { title: t('navbar.chemistryPromotion'), href: "https://www.youtube.com/watch?v=CgduFVGf6LI", icon: "Youtube" },
    { title: t('navbar.actuarialPromotion'), href: "https://aktuerya.hacettepe.edu.tr/duyurular/tanitim25.pdf", icon: "FileText" },
    { title: t('navbar.mathematicsPromotion'), href: "https://mat.hacettepe.edu.tr/duyurular/tanitim_sunum.pdf", icon: "FileText" },
    { title: t('navbar.statisticsPromotion'), href: "https://www.youtube.com/watch?v=rMbJeSIByPA", icon: "Youtube" },
];

// Sanal Tur verisi
const getVirtualTours = (t, currentLang) => {
    const prefix = currentLang === 'en' ? '/en' : '';
    return [
        { title: t('navbar.chemistryVirtualTour'), href: prefix + "/bolumler/kimya-bolumu-sanal-tur", icon: "Building2" },
    ];
};

const AccordionMenu = ({ setIsOpen }) => {
    const [activeIndex, setActiveIndex] = useState(null);
    const { t, i18n } = useTranslation();
    const navLinks = getNavLinks(t, i18n.language);

    const handleToggle = (index) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    return (
        <div className="accordion-menu">
            {navLinks.map((link, index) => (
                <div key={link.title} className="accordion-item">
                    {link.sublinks ? (
                        <button className="accordion-title" onClick={() => handleToggle(index)}>
                            <span>{link.title}</span>
                            <motion.div animate={{ rotate: activeIndex === index ? 180 : 0 }}>
                                <ChevronDown size={20} />
                            </motion.div>
                            </button>
                    ) : (
                        <Link to={link.href} className="accordion-title" onClick={() => setIsOpen(false)}>
                            <span>{link.title}</span>
                        </Link>
                    )}
                    <AnimatePresence>
                        {activeIndex === index && link.sublinks && (
                            <motion.ul
                                initial={{ maxHeight: 0, opacity: 0 }}
                                animate={{ maxHeight: 500, opacity: 1 }}
                                exit={{ maxHeight: 0, opacity: 0 }}
                                transition={{ duration: 0.4, ease: [0.2, 1, 0.8, 1] }}
                                className="accordion-content"
                                style={{ overflow: 'hidden' }}
                            >
                                {link.sublinks.map(sub => {
                                    const IconComponent = {
                                        Youtube: Youtube,
                                        FileText: FileText,
                                    }[sub.icon];
                                    return (
                                        <li key={sub.title}>
                                            {sub.external ? (
                                <a 
                                    href={sub.href} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    onClick={() => {
                                        setIsOpen(false);
                                        trackExternalClick(sub.title, sub.href);
                                    }}
                                >
                                                    {IconComponent && <IconComponent size={16} className="mr-2" />}
                                                    {sub.title}
                                                </a>
                                            ) : (
                                <Link 
                                    to={sub.href} 
                                    onClick={() => {
                                        setIsOpen(false);
                                        trackNavigation(sub.title, link.title);
                                    }}
                                >
                                                    {IconComponent && <IconComponent size={16} className="mr-2" />}
                                                    {sub.title}
                                                </Link>
                                            )}
                                        </li>
                                    );
                                })}
                            </motion.ul>
                        )}
                    </AnimatePresence>
                </div>
            ))}
        </div>
    );
};

const VerticalPanelMenu = ({ isOpen, setIsOpen, changeLanguage, currentLang }) => {
    const { t, i18n } = useTranslation();

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="vertical-menu-overlay"
                    onClick={() => setIsOpen(false)}
                >
                    <motion.div
                        initial={{ x: "100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "100%" }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="vertical-menu-panel"
                        style={{
                            right: 0,
                            left: 'auto',
                            width: '320px',
                            maxWidth: '90vw',
                            overflowY: 'auto',
                            maxHeight: '100vh',
                            backgroundColor: '#f8fafc',
                            background: 'linear-gradient(135deg, #f8fafc 60%, #e3e8ee 100%)'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="vertical-menu-header">
                            <div className="menu-logo">
                                <img
                                    src="https://fs.hacettepe.edu.tr/fenyeni/e-dosyalar/fenfaculty.png"
                                    alt="Fen Fakültesi Logosu"
                                    className="menu-logo-img"
                                />
                                <span className="menu-title">{t('navbar.facultyName')}</span>
                        </div>
                        <button
                            className="menu-close-btn"
                            onClick={() => setIsOpen(false)}
                        >
                            <XIcon size={20} />
                        </button>
                    </div>
                    <div className="p-3">
                        {(() => {
                            const sortedAnnouncements = [...DuyurularData].sort((a, b) => new Date(b.date) - new Date(a.date));
                            const latestAnnouncements = sortedAnnouncements.slice(0, 1);

                            const renderAnnouncement = (announcement, index) => {
                                const isExternal = announcement.link && announcement.link.startsWith('http');
                                const internalUrl = announcement.link || `/duyurular/${announcement.title.replace(/[^a-zA-Z0-9-_]/g, '').toLowerCase()}`;

                                if (isExternal) {
                                    return (
                                        <a
                                            key={index}
                                            href={announcement.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="block p-2 bg-blue-100 rounded-lg shadow-sm hover:bg-blue-200 transition-colors mb-2"
                                            onClick={() => {
                                                setIsOpen(false);
                                                trackExternalClick('Duyuru', announcement.link);
                                            }}
                                        >
                                            <div className="flex items-center justify-between mb-0.5">
                                                <div className="flex items-center">
                                                    <Bell size={12} className="text-blue-600 mr-1.5" />
                                                    <span className="text-blue-600 font-bold text-[10px] uppercase">
                                                        {index === 0 ? t('navbar.latestAnnouncement') : t('navbar.announcement')}
                                                    </span>
                                                </div>
                                                {announcement.isNew && (
                                                    <span className="px-1 py-0.5 rounded text-[9px] font-bold bg-red-500 text-white animate-pulse">
                                                        {t('navbar.new')}
                                                    </span>
                                                )}
                                            </div>
                                            <h3 className="text-[11px] font-semibold text-gray-800 leading-tight mb-0.5 line-clamp-2">
                                                {announcement.title}
                                            </h3>
                                            <p className="text-gray-500 text-[10px]">
                                                {new Date(announcement.date).toLocaleDateString('tr-TR', { year: 'numeric', month: 'short', day: 'numeric' })}
                                            </p>
                                        </a>
                                    );
                                }

                                return (
                                    <Link
                                        key={index}
                                        to={internalUrl}
                                        className="block p-2 bg-blue-100 rounded-lg shadow-sm hover:bg-blue-200 transition-colors mb-2"
                                        onClick={() => setIsOpen(false)}
                                    >
                                        <div className="flex items-center justify-between mb-0.5">
                                            <div className="flex items-center">
                                                <Bell size={12} className="text-blue-600 mr-1.5" />
                                                <span className="text-blue-600 font-bold text-[10px] uppercase">
                                                    {index === 0 ? t('navbar.latestAnnouncement') : t('navbar.announcement')}
                                                </span>
                                            </div>
                                            {announcement.isNew && (
                                                <span className="px-1 py-0.5 rounded text-[9px] font-bold bg-red-500 text-white animate-pulse">
                                                    {t('navbar.new')}
                                                </span>
                                            )}
                                        </div>
                                        <h3 className="text-[11px] font-semibold text-gray-800 leading-tight mb-0.5 line-clamp-2">
                                            {announcement.title}
                                        </h3>
                                        <p className="text-gray-500 text-[10px]">
                                            {new Date(announcement.date).toLocaleDateString('tr-TR', { year: 'numeric', month: 'short', day: 'numeric' })}
                                        </p>
                                    </Link>
                                );
                            };

                            return (
                                <div>
                                    {latestAnnouncements.length > 0 ? (
                                        latestAnnouncements.map((announcement, index) => 
                                            renderAnnouncement(announcement, index)
                                        )
                                    ) : (
                                        <div className="p-3 bg-gray-100 rounded-lg mb-2">
                                            <p className="text-gray-600 text-xs">{t('navbar.noAnnouncements')}</p>
                                        </div>
                                    )}
                                    <Link
                                        to={(i18n.language === 'en' ? '/en' : '') + "/duyurular"}
                                        className="flex items-center justify-center w-full bg-blue-600 text-white font-semibold py-1.5 px-3 rounded-lg hover:bg-blue-700 transition-colors text-xs"
                                        onClick={() => setIsOpen(false)}
                                    >
                                        <Bell size={14} className="mr-1.5" />
                                        <span>{t('navbar.allAnnouncements')}</span>
                                    </Link>
                                </div>
                            );
                        })()}
                    </div>

                    {/* Dil Değiştirme Bölümü - Sadece Mobil */}
                    <div className="px-3 pb-2 md:hidden">
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-2 border border-blue-100">
                            <div className="flex items-center justify-center mb-1.5">
                                <Globe2 size={14} className="text-blue-600 mr-1" />
                                <span className="text-[10px] font-semibold text-gray-700">
                                    Dil / Language
                                </span>
                            </div>
                            <div className="flex gap-1.5">
                                <button
                                    onClick={() => {
                                        changeLanguage('tr');
                                        setIsOpen(false);
                                    }}
                                    className={`flex-1 py-1.5 px-1.5 rounded-lg font-semibold text-[10px] transition-all duration-200 flex items-center justify-center gap-1 ${
                                        currentLang === 'tr'
                                            ? 'bg-blue-600 text-white shadow-md'
                                            : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                                    }`}
                                >
                                    <span className="text-sm">🇹🇷</span>
                                    <span>TR</span>
                                </button>
                                <button
                                    onClick={() => {
                                        changeLanguage('en');
                                        setIsOpen(false);
                                    }}
                                    className={`flex-1 py-1.5 px-1.5 rounded-lg font-semibold text-[10px] transition-all duration-200 flex items-center justify-center gap-1 ${
                                        currentLang === 'en'
                                            ? 'bg-blue-600 text-white shadow-md'
                                            : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                                    }`}
                                >
                                    <span className="text-sm">🇬🇧</span>
                                    <span>EN</span>
                                </button>
                            </div>
                        </div>
                    </div>

                        <AccordionMenu setIsOpen={setIsOpen} />
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isPromotionsOpen, setIsPromotionsOpen] = useState(false);
    const [isVirtualToursOpen, setIsVirtualToursOpen] = useState(false);
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const navLinks = getNavLinks(t, i18n.language);
    const departmentPromotions = getDepartmentPromotions(t);
    const virtualTours = getVirtualTours(t, i18n.language);

    const changeLanguage = (lang) => {
        let newPath = location.pathname;

        if (lang === 'en') {
            // İngilizce'ye çevir
            if (!newPath.startsWith('/en/') && newPath !== '/en') {
                newPath = newPath === '/' ? '/en' : '/en' + newPath;
            }
        } else {
            // Türkçe'ye çevir
            if (newPath.startsWith('/en/') || newPath === '/en') {
                newPath = newPath.replace(/^\/en/, '') || '/';
            }
        }

        navigate(newPath);
    };

    const currentLang = i18n.language;

    return (
        <>
            <nav className="main-navbar-transparent">
                <div className="navbar-container-transparent">
                    <Link to="/" className="navbar-logo-rebranded">
                        <img src="https://kkm.hacettepe.edu.tr/wp-content/uploads/2018/07/cropped-logo-hacettepe.png" alt="Hacettepe Üniversitesi Logosu" className="main-logo"/>
                        <div className="logo-text-rebranded">
                            <h1>{t('navbar.universityName')}</h1>
                            <h2>{t('navbar.facultyNameUpper')}</h2>
                        </div>
                        <img src="https://iysportal.hacettepe.edu.tr/templates/template3/images/yokak5.svg" alt="YÖKAK Logosu" className="yokak-logo"/>
                    </Link>
                    <div className="navbar-actions-transparent">
                        <div className="header-icons">
                            <a 
                                href="https://fenfakultesi.hacettepe.edu.tr" 
                                target="_blank" 
                                className="header-icon-link home-icon" 
                                title="Fakülte Anasayfa"
                                onClick={() => trackExternalClick('Fakülte Anasayfa', 'https://fenfakultesi.hacettepe.edu.tr')}
                            >
                                <Home size={20} />
                            </a>
                            <a 
                                href="https://www.instagram.com/hacettepefenfakultesi" 
                                target="_blank" 
                                className="header-icon-link instagram-icon" 
                                title="Instagram"
                                onClick={() => trackExternalClick('Instagram', 'https://www.instagram.com/hacettepefenfakultesi')}
                            >
                                <Instagram size={26} />
                            </a>
                            <a
                                href="https://www.youtube.com/@hacettepeuniversitesifenfa9147"
                                target="_blank"
                                className="header-icon-link youtube-icon"
                                title="YouTube"
                                onClick={() => trackExternalClick('YouTube', 'https://www.youtube.com/@hacettepeuniversitesifenfa9147')}
                            >
                                <Youtube size={26} />
                            </a>
                            <a
                                href="https://tr.linkedin.com/in/fen-fak%C3%BCltesi-hacettepe-%C3%BCniversitesi-32775a234"
                                target="_blank"
                                className="header-icon-link linkedin-icon"
                                title="LinkedIn"
                                onClick={() => trackExternalClick('LinkedIn', 'https://tr.linkedin.com/in/fen-fak%C3%BCltesi-hacettepe-%C3%BCniversitesi-32775a234')}
                            >
                                <Linkedin size={26} />
                            </a>
                            <a
                                href="https://www.hacettepe.edu.tr"
                                target="_blank"
                                className="header-icon-link"
                                title="Hacettepe Üniversitesi Anasayfa"
                                onClick={() => trackExternalClick('Hacettepe Anasayfa', 'https://www.hacettepe.edu.tr')}
                            >
                                <img src="https://kkm.hacettepe.edu.tr/wp-content/uploads/2018/07/cropped-logo-hacettepe.png" alt="Hacettepe İkonu" className="header-hacettepe-icon" />
                            </a>
                            <a 
                                href="mailto:fendek@hacettepe.edu.tr" 
                                className="header-icon-link mail-icon" 
                                title="E-posta Gönder"
                                onClick={() => trackButtonClick('E-posta', 'header')}
                            >
                                <Mail size={26} />
                            </a>
                            <a 
                                href="tel:+903122992080" 
                                className="header-icon-link phone-icon" 
                                title="Telefon Et"
                                onClick={() => trackButtonClick('Telefon', 'header')}
                            >
                                <Phone size={26} />
                            </a>
                            <a
                                href="/iletisim"
                                className="header-icon-link map-icon"
                                title="İletişim/Harita"
                                onClick={() => trackNavigation('İletişim', 'header')}
                            >
                                <MapPin size={26} />
                            </a>
                            <button
                                onClick={() => changeLanguage(currentLang === 'tr' ? 'en' : 'tr')}
                                className="header-icon-link language-switcher"
                                title={currentLang === 'tr' ? 'Switch to English' : 'Türkçe\'ye Geç'}
                                style={{
                                    background: 'rgba(255,255,255,0.15)',
                                    border: '1px solid rgba(255,255,255,0.4)',
                                    borderRadius: '9999px',
                                    cursor: 'pointer',
                                    padding: '6px 10px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    color: '#ffffff',
                                    fontWeight: '700',
                                    fontSize: '12px',
                                    lineHeight: 1
                                }}
                            >
                                <Globe size={18} />
                                <span>{currentLang === 'tr' ? 'EN' : 'TR'}</span>
                            </button>
                        </div>
                        
                        <div className="department-promotions-menu">
                            <button className="department-promotions-toggle" onClick={() => {
                                setIsPromotionsOpen(!isPromotionsOpen);
                                setIsVirtualToursOpen(false);
                            }}>
                                <GraduationCap size={16} />
                                {t('navbar.departmentPromotions')} <ChevronDown size={16} className={`transition-transform ${isPromotionsOpen ? 'rotate-180' : ''}`} />
                            </button>
                            <AnimatePresence>
                                {isPromotionsOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.2 }}
                                        className="department-promotions-dropdown"
                                    >
                                        {departmentPromotions.map((item, idx) => {
                                            const IconComponent = {
                                                Youtube: Youtube,
                                                FileText: FileText,
                                            }[item.icon];
                                            return (
                                                <a
                                                    key={idx}
                                                    href={item.href}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="dropdown-item"
                                                    onClick={() => trackExternalClick(`Bölüm Tanıtımı - ${item.title}`, item.href)}
                                                >
                                                    {IconComponent && <IconComponent size={16} className="mr-2" />}
                                                    {item.title}
                                                </a>
                                            );
                                        })}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        <div className="department-promotions-menu">
                            <button className="department-promotions-toggle" onClick={() => {
                                setIsVirtualToursOpen(!isVirtualToursOpen);
                                setIsPromotionsOpen(false);
                            }}>
                                <Building2 size={16} />
                                {t('navbar.virtualTours')} <ChevronDown size={16} className={`transition-transform ${isVirtualToursOpen ? 'rotate-180' : ''}`} />
                            </button>
                            <AnimatePresence>
                                {isVirtualToursOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.2 }}
                                        className="department-promotions-dropdown"
                                    >
                                        {virtualTours.map((item, idx) => {
                                            const IconComponent = {
                                                Building2: Building2,
                                            }[item.icon];
                                            return (
                                                <Link
                                                    key={idx}
                                                    to={item.href}
                                                    className="dropdown-item"
                                                    onClick={() => {
                                                        setIsVirtualToursOpen(false);
                                                        trackNavigation(`Sanal Tur - ${item.title}`, item.href);
                                                    }}
                                                >
                                                    {IconComponent && <IconComponent size={16} className="mr-2" />}
                                                    {item.title}
                                                </Link>
                                            );
                                        })}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                        <Link 
                            to="/duyurular" 
                            className="announcements-btn"
                            onClick={() => trackButtonClick('Duyurular', 'header')}
                        >
                            <Bell size={16} />
                            <span>{t('navbar.announcements')}</span>
                        </Link>
                        
                        <button className="menu-toggle-btn-transparent" onClick={() => setIsOpen(true)}>
                            <span>{currentLang === 'en' ? 'Menu' : 'Menü'}</span>
                            <MenuIcon />
                        </button>
                    </div>
                </div>
            </nav>
            <VerticalPanelMenu isOpen={isOpen} setIsOpen={setIsOpen} changeLanguage={changeLanguage} currentLang={currentLang} />
        </>
    );
};

export default Navbar;